import { useFeatureFlags } from "@hooks/http/use-feature-flag";

import { SetupSection } from "./components/setup-section";
import { Code, OrderedList, SubSubSectionHeader } from "./components/styled";
import { SubmitTicketButton } from "./components/submit-ticket-button";
import { TatariAddAwsCredsImage } from "./components/tatari-add-aws-creds-image";
import { TatariIdImage } from "./components/tatari-id-image";
import { type PlatformDetails } from "./platform-details";

export const TatariDetails: PlatformDetails = {
  northbeamDocUrl: "https://docs.northbeam.io/docs/tracking-for-tatari",
  officialDocUrl: "",
  additionalDetails: [
    {
      title: "What does the Tatari integration do?",
      content:
        /* eslint-disable max-len */
        "We pull spend and impressions metrics from Tatari, which enables our view-through modeling data. Tatari only tracks connected TV, not linear.",
    },
    {
      title: "Where can I view my Tatari data in Northbeam?",
      content:
        "Once our team has confirmed that your account is plugged in, we'll enable Tatari on your account. To see Tatari performance, reference a new attribution model called ’Clicks and Matchbacks’.",
    },
  ],
};
export const TatariSetup = ({
  connectionContent,
}: {
  connectionContent: React.ReactNode;
}) => {
  const { hasFeatureFlag } = useFeatureFlags();

  const displayTatariConnectionCard = hasFeatureFlag("enableTatariConnection");
  if (!displayTatariConnectionCard) {
    return <TatariManualSetup />;
  }
  return (
    <>
      <SetupSection title="Enable AWS Data Sharing">
        <p>
          Once we plug in your Tatari ID, we'll notify you and you should be
          able to insert the AWS credentials. This is how we fetch impression
          data.
        </p>
        <div className="flex flex-col gap-2">
          <SubSubSectionHeader>
            Make sure you’ve followed these steps:
          </SubSubSectionHeader>
          <OrderedList variant="upper-alpha">
            <>Log into Tatari via tatari.tv</>
            <>
              Click on ’Integrations’ in the hamburger menu in the upper right
              (number 2 in image below)
            </>
            <>Click on ’AWS Data Sharing’ (number 3 in image below)</>
            <div className="inline-table">
              Add an ‘AWS Account’ to grant access to your Tatari datasets via
              AWS S3 using the credentials found in the last step below.
            </div>
            <div className="inline-table">
              <div className="flex flex-col gap-2">
                <span>Click ’Add’</span>
                <TatariAddAwsCredsImage />
              </div>
            </div>
          </OrderedList>
        </div>
      </SetupSection>
      <SetupSection title="Get your ‘Tatari ID’">
        <div className="inline-table">
          <div className="flex flex-col gap-2">
            <span>
              Locate your ‘Tatari ID’ in your Tatari dashboard URL slug
            </span>
            <i className="font-normal">Number 1 in the image below</i>
            <TatariIdImage />
          </div>
        </div>
      </SetupSection>
      <SetupSection title="Connect your Tatari Account">
        {connectionContent}
      </SetupSection>
    </>
  );
};

const TatariManualSetup = () => (
  <>
    <SetupSection title="Locate your Northbeam Client ID">
      <p>
        Before starting, it's important to have the your Northbeam Client ID
        ready
      </p>
      <OrderedList variant="upper-alpha" className="font-semibold">
        <>
          Locate your Northbeam Client ID
          <OrderedList className="font-normal" indent={1}>
            <>Log into Northbeam</>
            <>
              Find your Northbeam Client ID in the your browser’s URL slug{" "}
              <div className="flex gap-2 mt-2">
                <i>Example:</i>
                <Code>
                  <span className="text-interstellar">
                    dashboard.northbeam.io/
                  </span>
                  87aaeb29-3b46-4ddf-b824-11e6e3b9d3a7
                </Code>
              </div>
            </>
          </OrderedList>
        </>
      </OrderedList>
    </SetupSection>
    <SetupSection title="Share your ‘Tatari ID’ with Northbeam">
      <OrderedList variant="upper-alpha" className="font-semibold space-y-6">
        <div className="inline-table">
          <div className="flex flex-col gap-2">
            <span>
              Locate your ‘Tatari ID’ in your Tatari dashboard URL slug
            </span>
            <i className="font-normal">Number 1 in the image below</i>
            <TatariIdImage />
          </div>
        </div>
        <div className="inline-table">
          <div className="flex flex-col gap-2">
            <span>
              Submit a ticket to share your ‘Tatari ID’ with northbeam
            </span>
            <div>
              <SubmitTicketButton />
            </div>
          </div>
        </div>
      </OrderedList>
    </SetupSection>
    <SetupSection title="Insert AWS credentials">
      <p>
        Once we plug in your Tatari ID, we'll notify you and you should be able
        to insert the AWS credentials. This is how we fetch impression data.
      </p>
      <div className="flex flex-col gap-2">
        <SubSubSectionHeader>
          Make sure you’ve followed these steps:
        </SubSubSectionHeader>
        <OrderedList variant="upper-alpha">
          <>Log into Tatari via tatari.tv</>
          <>
            Click on ’Integrations’ in the hamburger menu in the upper right
            (number 2 in image below)
          </>
          <>Click on ’AWS Data Sharing’ (number 3 in image below)</>
          <div className="inline-table">
            Add an ‘AWS Account’ to grant access to your Tatari datasets via AWS
            S3 using the following credentials:
            <OrderedList variant="disc" className="mt-2">
              {[
                {
                  key: "AWS Account ID",
                  value: "892581138023",
                },
                {
                  key: "Type",
                  value: "User",
                },
                {
                  key: "Name",
                  value: (
                    <>
                      <span className="text-interstellar">nb-client-gen__</span>
                      [Northbeam-Client-ID]
                      <span className="text-interstellar">__tatari</span>
                    </>
                  ),
                },
              ].map((item, index) => (
                <div key={index} className="inline-table">
                  <div className="flex gap-2">
                    <span>{item.key}:</span>
                    <Code>{item.value}</Code>
                  </div>
                </div>
              ))}
            </OrderedList>
            <div className="mt-2 flex gap-2">
              <i>Name example</i>
              <Code>
                nb-client-gen__87aaeb29-3b46-4ddf-b824-11e6e3b9d3a7__tatari
              </Code>
            </div>
          </div>
          <div className="inline-table">
            <div className="flex flex-col gap-2">
              <span>Click ’Add’</span>
              <TatariAddAwsCredsImage />
            </div>
          </div>
        </OrderedList>
      </div>
      <div className="flex gap-2">
        <span>Note:</span>
        <span>
          If you see an error when submitting the credentials ("Invalid ARNs"),
          it's likely because we haven't plugged in your Tatari ID into our
          backend yet. We'll let you know when it's plugged in.
        </span>
      </div>
    </SetupSection>
  </>
);
